<template>
  <OnClickOutside
    ref="dropdownContainer"
    class="dropdown-container"
    :options="onClickOutsideOptions"
    @trigger="handleClickOutside"
  >
    <button ref="dropdownTrigger" @click.stop="toggleDropdown">
      <slot name="trigger" />
    </button>

    <div
      v-show="isOpen"
      ref="dropdownContent"
      :class="[contentClassName, { 'dropdown-fullscreen': isFullscreen }]"
      :style="contentStyles"
    >
      <button v-if="isFullscreen" @click.stop="closeDropdown">
        <slot name="mobile-close-button" />
      </button>

      <button v-else @click.stop="closeDropdown">
        <slot name="close-button" />
      </button>

      <slot name="content" />
    </div>
  </OnClickOutside>
</template>

<script>
import { computePosition, offset, flip, shift } from "@floating-ui/dom";
import { OnClickOutside } from "@vueuse/components";

export default {
  name: "GlobalUIDropdown",
  components: { OnClickOutside },
  props: {
    placement: {
      type: String,
      default: "bottom-start",
      validator(value) {
        return [
          "top",
          "top-start",
          "top-end",
          "right",
          "right-start",
          "right-end",
          "bottom",
          "bottom-start",
          "bottom-end",
          "left",
          "left-start",
          "left-end",
        ].includes(value);
      },
    },
    offset: {
      type: Number,
      default: 0,
    },
    shiftPadding: {
      type: Number,
      default: 0,
    },
    fullScreenOnMobile: {
      type: Boolean,
      default: false,
    },
    contentClassName: {
      type: String,
      default: "",
    },
    contentStyles: {
      type: Object,
      default() {},
    },
    teleportTarget: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      isOpen: false,
      floatingInstance: null, // Reference to the floating instance
      onClickOutsideOptions: {
        ignore: [],
      },
      originalParent: null,
    };
  },
  mounted() {
    this.$nextTick(() => {
      // Calculate position initially to prevent first opening to appear outside the viewport
      this.updatePosition();

      // Set ignore option for OnCLickOutside after refs have been loaded
      if (this.$refs.dropdownContent && this.$refs.dropdownTrigger) {
        this.onClickOutsideOptions.ignore = [
          this.$refs.dropdownTrigger,
          this.$refs.dropdownContent,
        ];
      }
    });
  },
  computed: {
    isFullscreen() {
      // Check if fullscreenOnMobile is true and if the screen width is small (using our tablet breakpoint here)
      return this.fullScreenOnMobile && window?.innerWidth <= 1033;
    },
  },
  methods: {
    toggleDropdown() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.updatePosition();
        this.preventBodyScrolling();
        if (this.isFullscreen) {
          this.moveToTeleportTarget();
        }
      }
    },
    closeDropdown() {
      this.isOpen = false;
      this.allowBodyScrolling();
      this.resetDropdown();
    },
    handleClickOutside() {
      if (this.isFullscreen) return;
      this.closeDropdown();
    },
    preventBodyScrolling() {
      if (this.isFullscreen) {
        document.body.style.overflow = "hidden";
      }
    },
    allowBodyScrolling() {
      if (this.isFullscreen) {
        document.body.style.overflow = "auto";
      }
    },
    moveToTeleportTarget() {
      if (!this.$refs.dropdownContent) return;

      // Save the original parent
      this.originalParent = this.$refs.dropdownTrigger.parentNode;

      // Move dropdown into provided teleport target to prevent positioning issues
      document
        .querySelector(this.teleportTarget)
        .appendChild(this.$refs.dropdownContent);
    },
    resetDropdown() {
      if (this.originalParent && this.$refs.dropdownContent) {
        // Restore dropdown to its original parent
        this.originalParent.appendChild(this.$refs.dropdownContent);
      }
    },
    async updatePosition() {
      if (
        !this.$refs.dropdownTrigger ||
        !this.$refs.dropdownContent ||
        this.isFullscreen
      )
        return;

      this.floatingInstance = await computePosition(
        this.$refs.dropdownTrigger,
        this.$refs.dropdownContent,
        {
          placement: this.placement,
          middleware: [
            offset(this.offset),
            flip(),
            shift({ padding: this.shiftPadding }),
          ],
        },
      );

      const { x, y } = this.floatingInstance;
      Object.assign(this.$refs.dropdownContent.style, {
        left: `${x}px`,
        top: `${y}px`,
        position: "absolute",
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.dropdown-container {
  display: inline-block;
}

.dropdown-fullscreen {
  position: fixed;
  z-index: 90;
  top: var(--cr-nav-height);
  left: 0;

  overflow: hidden;

  width: 100%;
  height: calc(100dvh - var(--cr-nav-height));
}

button {
  @include reset-button;
}
</style>
